import { BreadcrumbsNavigation } from "@/components/common/BreadcrumbsNavigation/BreadcrumbsNavigation"
import { PdfIntelligenceViewer } from "@/components/common/PdfViewer/PdfIntelligenceViewer"
import {
  SplitView,
  SplitViewSection,
} from "@/components/common/SplitView/SplitView"
import { SplitViewQuestionHeader } from "@/components/common/SplitView/SplitViewQuestionHeader"
import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import { CollapsibleQuestionnaireList } from "@/components/feature/CollapsibleQuestionnaireList/CollapsibleQuestionnaireList"
import { QuestionItem } from "@/components/feature/CollapsibleQuestionnaireList/QuestionItem/QuestionItem"
import { AnsweringContextDialog } from "@/components/feature/Modals/AnsweringContextDialog/AnsweringContextDialog"
import { ContinueLaterModal } from "@/components/feature/Modals/ContinueLaterModal/ContinueLaterModal"
import type { SelectDocumentsDialogSelection } from "@/components/feature/Modals/SelectDocumentsDialog/SelectDocumentsDialog"
import {
  readCompanyProfilePreference,
  SelectDocumentsDialog,
} from "@/components/feature/Modals/SelectDocumentsDialog/SelectDocumentsDialog"
import { QuestionnaireHeaderActions } from "@/components/feature/QuestionnaireHeaderActions/QuestionnaireHeaderActions"
import { Layout } from "@/components/layout"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs/useBreadcrumbs"
import { useHasSelectableQuestionnaireDocuments } from "@/hooks/useHasSelectableQuestionnaireDocuments/useHasSelectableQuestionnaireDocuments"
import { usePermissionGuard } from "@/hooks/usePermissionGuard"
import { useQuestionnaireAutofill } from "@/hooks/useQuestionnaireAutofill/useQuestionnaireAutofill"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { ErrorPage } from "@/pages/ErrorPage"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import { isQuestionnaireNotFound } from "@/services/api/questionnaireQueryPolicy"
import {
  useGetOriginalFile,
  useGetQuestionnaireAnsweringContext,
  useGetQuestionnaireData,
  useSaveQuestionnaireFilterConfig,
} from "@/services/api/questionnaireService"
import { isLimitError } from "@/utils/limitErrors"
import { reduceNumTotalQuestions } from "@/utils/questionnaire/reduceQuestions"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"

export const PdfQuestionsPage = () => {
  const { translate } = useTranslation()
  const taggingFlowEnabled = useFeatureFlag("KB_TAGGING_FLOW")
  const answeringContextEnabled = useFeatureFlag("ANSWERING_CONTEXT")
  const [isAnsweringContextOpen, setIsAnsweringContextOpen] = useState(false)
  const { hasSelectableQuestionnaireDocuments } =
    useHasSelectableQuestionnaireDocuments(taggingFlowEnabled)
  const { breadcrumbs } = useBreadcrumbs()
  const navigate = useNavigate()
  const [numTotalQuestions, setNumTotalQuestions] = useState(0)
  const [answerGenerationInProgress, setAnswerGenerationInProgress] =
    useState(false)
  const [selectedDocumentsCount, setSelectedDocumentsCount] = useState(0)
  const [savedSelectedTagIds, setSavedSelectedTagIds] = useState<string[]>([])
  const [excludeExpired, setExcludeExpired] = useState(true)
  const [useCompanyProfile, setUseCompanyProfile] = useState(
    readCompanyProfilePreference,
  )

  const { questionnaireId } = useParams<Record<string, string | undefined>>()
  const {
    data: questionnaire,
    isPending: isQuestionnaireDataPending,
    isFetched: isQuestionnaireFetched,
    isError: isQuestionnaireError,
    error: questionnaireError,
    refetch: refetchQuestionnaire,
  } = useGetQuestionnaireData(questionnaireId, { staleTime: 0, gcTime: 0 })

  const hasGeneratedAnswers = !!questionnaire?.sections.some((section) =>
    section.responsePairs.some((pair) => !!pair.response),
  )

  // Answering context only applies to v2 (ingested) questionnaires; a legacy
  // questionnaire's saved context would never reach the answering pipeline.
  // The answering-context endpoints require QUEST_RUN; viewers without it get neither the
  // button nor the query behind it.
  const { canRun: canRunQuestionnaire } = usePermissionGuard(
    PERMISSIONS.QUEST_RUN,
  )
  const showAnsweringContextButton =
    answeringContextEnabled &&
    canRunQuestionnaire &&
    questionnaire?.ingestionStatus != null

  const { data: answeringContext } = useGetQuestionnaireAnsweringContext(
    questionnaireId,
    showAnsweringContextButton,
  )
  const answeringContextSaved = !!(
    answeringContext?.text || answeringContext?.terms.length
  )

  const { data: fileUrl } = useGetOriginalFile(
    questionnaireId,
    !!questionnaire && !isQuestionnaireNotFound(questionnaireError),
  )

  const { runAutofillWithRedirect, isPending: isAutofillPending } =
    useQuestionnaireAutofill({
      questionnaireId,
      questionnaire,
      navigate,
      refetchQuestionnaire,
    })
  const saveQuestionnaireFilterConfig = useSaveQuestionnaireFilterConfig()

  const [isContinueLaterModalOpen, setIsContinueLaterModalOpen] =
    useState(false)
  const [isSelectDocumentsOpen, setIsSelectDocumentsOpen] = useState(false)

  const handleOpenContinueLaterModal = () => {
    setIsContinueLaterModalOpen(true)
  }

  const handleTriggerAutofill = async () => {
    try {
      if (!questionnaire || !questionnaireId) {
        toast.error(translate("QUESTIONNAIRE_QUESTIONS/TOAST_AUTOFILL_ERROR"))
        return
      }

      setAnswerGenerationInProgress(true)

      trackEvent(AnalyticsEvent.AUTO_FILL_STARTED, {
        questionnaire_id: questionnaireId,
        question_count: numTotalQuestions,
        source: "PDF",
      })
      await runAutofillWithRedirect(useCompanyProfile)
    } catch (error) {
      // The axios interceptor already showed the limit toast with its upgrade action
      if (!isLimitError(error)) {
        toast.error(translate("QUESTIONNAIRE_QUESTIONS/TOAST_AUTOFILL_ERROR"))
      }
      trackEvent(AnalyticsEvent.AUTO_FILL_FAILED, {
        questionnaire_id: questionnaireId,
        source: "PDF",
      })
    } finally {
      setAnswerGenerationInProgress(false)
    }
  }

  const handleSaveDocumentSelection = async (
    selection: SelectDocumentsDialogSelection,
  ) => {
    try {
      await saveQuestionnaireFilterConfig.mutateAsync({
        questionnaireId,
        body: {
          categoryFilters: selection.categoryFilters,
          includeUnclassified: selection.includeUnclassified,
          excludeExpired: selection.excludeExpired,
        },
      })

      setSelectedDocumentsCount(selection.selectedFiles.length)
      setSavedSelectedTagIds(selection.selectedTagIds)
      setExcludeExpired(selection.excludeExpired)
      setUseCompanyProfile(selection.useCompanyProfile)
    } catch (error) {
      // The axios interceptor already showed the limit toast with its upgrade action
      if (!isLimitError(error)) {
        toast.error(translate("ERROR/SOMETHING_WENT_WRONG"))
      }
      throw new Error("Failed to save questionnaire filter config", {
        cause: error,
      })
    }
  }

  const handleClearDocumentSelection = () => {
    setSelectedDocumentsCount(0)
    setSavedSelectedTagIds([])
    setExcludeExpired(true)
  }

  // Handle questionnaire data loading
  useEffect(() => {
    if (isQuestionnaireError && !isQuestionnaireNotFound(questionnaireError)) {
      toast.error(translate("QUESTIONNAIRE_QUESTIONS/TOAST_GET_ERROR"))
    } else if (isQuestionnaireFetched && questionnaire) {
      setNumTotalQuestions(reduceNumTotalQuestions(questionnaire))
    }
  }, [isQuestionnaireFetched, questionnaire, isQuestionnaireError, translate])

  if (isQuestionnaireNotFound(questionnaireError))
    return <ErrorPage variant="not-found" withLayout />

  return (
    <Layout
      defaultSidebarState={"closed"}
      headerLeftElement={<BreadcrumbsNavigation breadcrumbs={breadcrumbs} />}
      headerRightElement={
        <QuestionnaireHeaderActions
          continueLaterLabel={translate("QUESTIONNAIRE/CONTINUE_LATER")}
          selectDocumentsLabel={translate("SELECT_DOCUMENTS/OPEN_BUTTON")}
          showSelectDocumentsButton={hasSelectableQuestionnaireDocuments}
          generateAnswersLabel={translate(
            "QUESTIONNAIRE_QUESTIONS/GENERATE_ANSWERS",
          )}
          selectDocumentsBadgeCount={selectedDocumentsCount}
          onContinueLater={handleOpenContinueLaterModal}
          onSelectDocuments={() => setIsSelectDocumentsOpen(true)}
          onGenerateAnswers={handleTriggerAutofill}
          isGenerateDisabled={
            isQuestionnaireDataPending ||
            isAutofillPending ||
            answerGenerationInProgress
          }
          showGenerateLoader={answerGenerationInProgress}
          showAnsweringContextButton={showAnsweringContextButton}
          answeringContextLabel={translate("ANSWERING_CONTEXT/OPEN_BUTTON")}
          onAnsweringContext={() => setIsAnsweringContextOpen(true)}
          answeringContextSaved={answeringContextSaved}
        />
      }
    >
      {isContinueLaterModalOpen && (
        <ContinueLaterModal
          setIsContinueLaterModalOpen={setIsContinueLaterModalOpen}
        />
      )}

      {hasSelectableQuestionnaireDocuments ? (
        <SelectDocumentsDialog
          questionnaireId={questionnaireId}
          open={isSelectDocumentsOpen}
          onOpenChange={setIsSelectDocumentsOpen}
          initialSelectedTagIds={savedSelectedTagIds}
          initialHideExpired={excludeExpired}
          onConfirm={handleSaveDocumentSelection}
          onClearSelection={handleClearDocumentSelection}
        />
      ) : null}

      {answeringContextEnabled ? (
        <AnsweringContextDialog
          questionnaireId={questionnaireId}
          open={isAnsweringContextOpen}
          onOpenChange={setIsAnsweringContextOpen}
          hasAnswers={hasGeneratedAnswers}
          onRegenerate={() => {
            void handleTriggerAutofill()
          }}
        />
      ) : null}

      <SplitView>
        <SplitViewSection>
          <PdfIntelligenceViewer fileUrl={fileUrl} highlightBoxesArray={[]} />
        </SplitViewSection>

        <SplitViewSection className="px-M">
          <div className="flex h-full flex-col">
            <SplitViewQuestionHeader
              titleKey="BREADCRUMBS_NAVIGATION/TITLE_QUESTIONS"
              countLabelKey="QUESTIONNAIRE_QUESTIONS/COUNT_TOTAL_QUESTIONS"
              countValueDisplay={
                <Typography
                  as="span"
                  variant="textM"
                  className="text-yellow1"
                  dataTestId={"questionnaire-total"}
                >
                  {numTotalQuestions}
                </Typography>
              }
            />
            <div className="flex-1 overflow-y-auto pt-XS pr-4">
              {questionnaire?.sections.map((section, index) => {
                return (
                  <CollapsibleQuestionnaireList
                    key={`${section.id}-${index}`}
                    dataTestId="questionnaire-section"
                    sectionIndex={index}
                    sectionTitle={section.name}
                    sectionCountDisplay={
                      <Typography as="p" variant="textM">
                        {translate(
                          "QUESTIONNAIRE_QUESTIONS/COUNT_SECTION_QUESTIONS",
                        )}{" "}
                        <Typography
                          as="span"
                          variant="textM"
                          className="text-yellow1"
                        >
                          {section.responsePairs.length}
                        </Typography>
                      </Typography>
                    }
                  >
                    {section.responsePairs.map((pair, index) => {
                      return (
                        <QuestionItem
                          key={`${pair.id}-${index}`}
                          questionnaireId={questionnaireId || ""}
                          responsePair={pair}
                          editEnabled={false}
                        />
                      )
                    })}
                  </CollapsibleQuestionnaireList>
                )
              })}
            </div>
          </div>
        </SplitViewSection>
      </SplitView>
    </Layout>
  )
}
